import { joiResolver } from '@hookform/resolvers/joi';
import { Button } from 'components/atoms/Button';
import DatePicker from 'components/atoms/DatePicker/DatePicker.component';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import { Select } from 'components/atoms/Form/Select';
import SimpleAlertV2 from 'components/atoms/SimpleAlertV2.component';
import dayjs from 'dayjs';
import Joi from 'joi';
import type { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  StandingOrderFrequency,
  type Connector,
} from 'types/transactions.types';
import { isMainAccountableBank, TransferFieldNames } from './helpers';
import type { TransferSchedule } from './MoneyTransfer';

type FormData = {
  [TransferFieldNames.period]: StandingOrderFrequency;
  [TransferFieldNames.firstExecutionDate]: Date;
  [TransferFieldNames.lastExecutionDate]?: Date;
};

const TransferScheduleForm: FC<{
  sourceAccount?: Connector;
  defaultValues: TransferSchedule;
  onSubmit: (values: TransferSchedule) => void;
  isInternalTransfer?: boolean;
}> = ({
  defaultValues,
  onSubmit,
  sourceAccount,
  isInternalTransfer = false,
}) => {
  const { t } = useTranslation();

  // Convert TransferSchedule to FormData
  const formDefaultValues: FormData = {
    [TransferFieldNames.period]:
      defaultValues.period || StandingOrderFrequency.OneTime,
    [TransferFieldNames.firstExecutionDate]:
      defaultValues.firstExecutionDate || new Date(),
    [TransferFieldNames.lastExecutionDate]: defaultValues.lastExecutionDate,
  };
  const formAPI = useForm<FormData>({
    defaultValues: formDefaultValues,
    resolver: joiResolver(
      Joi.object({
        [TransferFieldNames.period]: Joi.string().required(),
        [TransferFieldNames.firstExecutionDate]: Joi.date()
          .required()
          .min(dayjs().startOf('day').toDate())
          .custom((value, helpers) => {
            const period =
              helpers.state.ancestors[0][TransferFieldNames.period];

            if (
              period === StandingOrderFrequency.Monthly &&
              dayjs(value).date() > 28
            ) {
              return helpers.error('date.maxDay');
            }
            return value;
          })
          .when(TransferFieldNames.period, {
            is: StandingOrderFrequency.OneTime,
            then: Joi.date().max(dayjs().add(1, 'year').toDate()),
          })
          .messages({
            'date.min': t(
              'accountable_banking.scheduled_payments.schedule_form.date.future',
              {
                label: t(
                  'accountable_banking.scheduled_payments.schedule_form.start_date',
                ),
              },
            ),
            'date.max': t(
              'accountable_banking.scheduled_payments.schedule_form.date.max_year',
            ),
            'date.maxDay': t(
              'accountable_banking.scheduled_payments.schedule_form.date.max_day',
            ),
          }),
        [TransferFieldNames.lastExecutionDate]: Joi.date()
          .optional()
          .min(Joi.ref(TransferFieldNames.firstExecutionDate))
          .messages({
            'date.min': t(
              'accountable_banking.scheduled_payments.schedule_form.date.min2',
            ),
          }),
      }),
    ),
  });
  const periodValue = formAPI.watch(
    TransferFieldNames.period as keyof FormData,
  ) as StandingOrderFrequency;

  const fromMainAccount = isMainAccountableBank(sourceAccount);

  // Determine which frequency options are allowed based on transfer rules:
  // 1. Scheduled transfers (OneTime future): from any account → only to external
  // 2. Standing Orders (aka daily, weekly, monthly): from main account only → to external OR internal
  const areScheduledTransfersAllowed = !isInternalTransfer; // Only to external
  const areStandingOrdersAllowed = fromMainAccount; // Only from main account

  // For internal transfers, OneTime is allowed but date must be restricted to today
  const isOneTimeAndInternalTransfer =
    periodValue === StandingOrderFrequency.OneTime && isInternalTransfer;

  const handleSubmit = (formData: FormData) => {
    // Convert FormData back to TransferSchedule
    const scheduleData: TransferSchedule = {
      period: formData[TransferFieldNames.period],
      firstExecutionDate: formData[TransferFieldNames.firstExecutionDate],
      lastExecutionDate: formData[TransferFieldNames.lastExecutionDate],
    };
    onSubmit(scheduleData);
  };

  return (
    <FormProvider {...formAPI}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          formAPI.handleSubmit(handleSubmit)(e);
        }}
      >
        <div>
          <div className="flex items-center justify-center mb-5">
            <p className="font-semibold">
              {t('accountable_banking.scheduled_payments.schedule_form.when')}
            </p>
          </div>

          <FieldConnector
            name={TransferFieldNames.period}
            label={t('frequency')}
          >
            {({ field: { onChange, value, ...rest } }) => {
              const frequencyOptions = [
                {
                  label: t('OneTime'),
                  value: StandingOrderFrequency.OneTime,
                },
                areStandingOrdersAllowed && {
                  label: t('Monthly'),
                  value: StandingOrderFrequency.Monthly,
                },
                areStandingOrdersAllowed && {
                  label: t('Weekly'),
                  value: StandingOrderFrequency.Weekly,
                },
                areStandingOrdersAllowed && {
                  label: t('Daily'),
                  value: StandingOrderFrequency.Daily,
                },
              ].filter(Boolean);

              return (
                <Select
                  options={frequencyOptions as any}
                  value={value}
                  onChange={(option) => {
                    onChange(option.value);

                    // If OneTime is selected for internal transfer, reset date to today
                    if (
                      option.value === StandingOrderFrequency.OneTime &&
                      isInternalTransfer
                    ) {
                      formAPI.setValue(
                        TransferFieldNames.firstExecutionDate,
                        dayjs().startOf('day').toDate(),
                      );
                    }
                  }}
                  {...rest}
                />
              );
            }}
          </FieldConnector>

          {isOneTimeAndInternalTransfer ? null : (
            <div className="flex items-start space-x-4 my-3">
              <FieldConnector
                className="flex-1 min-w-0"
                label={t(
                  'accountable_banking.scheduled_payments.schedule_form.start_date',
                )}
                name={TransferFieldNames.firstExecutionDate}
              >
                {({ field }) => {
                  return (
                    <DatePicker
                      {...field}
                      selected={field.value}
                      minDate={dayjs().startOf('day').toDate()}
                      maxDate={
                        isInternalTransfer
                          ? dayjs().endOf('day').toDate() // Internal: today only
                          : // : periodValue === StandingOrderFrequency.OneTime
                            //   ? dayjs().add(1, 'year').toDate() // External OneTime: up to 1 year
                            undefined // Recurring: no max
                      }
                      onChange={(date) => {
                        // Keep as Date object to match FormData type
                        field.onChange(date);

                        // Only reset to today for internal transfers
                        if (
                          isInternalTransfer &&
                          !dayjs(date).isSame(dayjs(), 'day')
                        ) {
                          field.onChange(dayjs().startOf('day').toDate());
                        }
                      }}
                      placeholder={dayjs().format('YYYY-MM-DD')}
                    />
                  );
                }}
              </FieldConnector>

              <FieldConnector
                className="flex-1 min-w-0"
                label={t(
                  'accountable_banking.scheduled_payments.schedule_form.end_date',
                )}
                name={TransferFieldNames.lastExecutionDate}
                showField={periodValue !== StandingOrderFrequency.OneTime}
              >
                {({ field }) => (
                  <DatePicker
                    {...field}
                    selected={field.value}
                    onChange={(date) => field.onChange(date)}
                    disabled={false}
                  />
                )}
              </FieldConnector>
            </div>
          )}

          {!areStandingOrdersAllowed &&
          periodValue !== StandingOrderFrequency.OneTime ? (
            <SimpleAlertV2
              subtitle={t(
                'accountable_banking.scheduled_payments.schedule_form.standing_orders_main_only',
                'Standing orders (recurring transfers) can only be created from the main account.',
              )}
              type="info"
            />
          ) : null}

          <Button
            type="submit"
            className="w-full mt-6"
            disabled={
              periodValue !== StandingOrderFrequency.OneTime &&
              !areStandingOrdersAllowed
            }
          >
            {t('continue')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default TransferScheduleForm;
